import { useState, useMemo, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import type { SortDescriptor } from 'react-aria-components'
import { TableBody as AriaTableBody } from 'react-aria-components'
import { Search } from 'lucide-react'
import { PlusIcon, Send01Icon } from '@untitledui-icons/react/line'
import { Table, TableCard } from '@/components/application/table/table'
import { PaginationTable } from '@/components/application/pagination/pagination'
import { Button } from '@/components/base/buttons'
import { Input } from '@/components/ui/Input'
import { StatusBadge } from '@/components/data/StatusBadge'
import { LoadingSpinner, EmptyState } from '@/components/common'
import { useListPageSearch } from '@/hooks/useListPageSearch'
import { useUserList, useResendInvite } from '@/hooks/api/useUserManagement'
import type { User, UserListFilters } from '@/types/user'

/**
 * Users list page - can be accessed under /customers/:customerId/users or /partners/:partnerId/users
 */
export function UsersPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { customerId, partnerId } = useParams<{
    customerId?: string
    partnerId?: string
  }>()

  // Build base path for navigation
  const basePath = customerId
    ? `/customers/${customerId}/users`
    : partnerId
      ? `/partners/${partnerId}/users`
      : '/users'

  const {
    searchQuery,
    handleSearch,
    filters: urlFilters,
    hasActiveFilters,
    page,
    perPage,
    handlePageChange,
    handlePageSizeChange,
  } = useListPageSearch<{ q?: string }>({
    paramKey: 'q',
    debounceMs: 300,
  })

  // Build filters — combine URL-derived search with route-scoped IDs
  const filters = useMemo((): UserListFilters => {
    const result: UserListFilters = {}
    if (customerId) {
      result.customer_id = parseInt(customerId, 10)
    }
    if (partnerId) {
      result.partner_id = parseInt(partnerId, 10)
    }
    if (urlFilters.q) {
      result.q = urlFilters.q
    }
    return result
  }, [customerId, partnerId, urlFilters.q])

  // Sort state
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: 'id',
    direction: 'ascending',
  })

  // Fetch users
  const { data, isLoading, isFetching } = useUserList({
    filters,
    page,
    perPage,
    sort: {
      field: sortDescriptor.column as string,
      order: sortDescriptor.direction === 'ascending' ? 'ASC' : 'DESC',
    },
  })

  const users = data?.data || []
  const totalCount = data?.total || 0
  const totalPages = Math.ceil(totalCount / perPage)

  // Handle row click
  const handleRowClick = useCallback(
    (user: User) => {
      navigate(`${basePath}/${user.id}`)
    },
    [navigate, basePath]
  )

  // Handle sort change
  const handleSortChange = useCallback((descriptor: SortDescriptor) => {
    setSortDescriptor(descriptor)
  }, [])

  // Resend invite mutation
  const resendInvite = useResendInvite()
  const [resendingUserId, setResendingUserId] = useState<number | null>(null)

  // Get user status
  const getUserStatus = (user: User): 'active' | 'pending' | 'disabled' => {
    if (user.status) return user.status
    if (user.is_active) return 'active'
    return 'pending'
  }

  const hasFiltersOrSearch = hasActiveFilters || !!searchQuery
  const showInitialLoading = isLoading && !data && !hasFiltersOrSearch

  if (showInitialLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  // Empty state — only when there's no search filter active (otherwise keep search visible)
  if (!data && !hasFiltersOrSearch && users.length === 0) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <EmptyState
          title={t('resources.users.empty')}
          description={t('resources.users.empty_description')}
          action={
            <div className="flex items-center gap-3">
              <Button color="secondary" onPress={() => navigate(`${basePath}/create`)} iconLeading={PlusIcon}>
                {t('resources.users.create')}
              </Button>
              <Button color="primary" onPress={() => navigate(`${basePath}/invite`)} iconLeading={Send01Icon}>
                {t('resources.users.invite_user')}
              </Button>
            </div>
          }
        />
      </div>
    )
  }

  return (
    <TableCard.Root size="sm" className="flex h-full flex-col">
      <TableCard.Header
        title={t('resources.users.overview')}
        badge={<span className="text-md font-normal text-gray-500 dark:text-gray-400">({totalCount})</span>}
        contentTrailing={
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <Input
              type="text"
              value={searchQuery}
              onChange={handleSearch}
              placeholder={t('resources.users.search_placeholder')}
              leftIcon={<Search className="h-5 w-5" />}
              className="w-40 sm:w-56"
            />
            <Button color="secondary" size="md" onPress={() => navigate(`${basePath}/create`)} iconLeading={PlusIcon}>
              <span className="hidden sm:inline">{t('resources.users.create')}</span>
            </Button>
            <Button color="primary" size="md" onPress={() => navigate(`${basePath}/invite`)} iconLeading={Send01Icon}>
              <span className="hidden sm:inline">{t('resources.users.invite_user')}</span>
            </Button>
          </div>
        }
      />

      {/* Loading bar */}
      {isFetching && (
        <div className="h-1 w-full overflow-hidden bg-gray-100">
          <div className="h-full w-1/3 animate-pulse bg-brand-500" />
        </div>
      )}

      <div className="flex-1 overflow-auto">
        <Table
          aria-label={t('resources.users.overview')}
          sortDescriptor={sortDescriptor}
          onSortChange={handleSortChange}
        >
          <Table.Header>
            <Table.Head id="id" label={t('resources.users.fields.id')} allowsSorting isRowHeader />
            <Table.Head id="first_name" label={t('resources.users.fields.first_name')} allowsSorting />
            <Table.Head id="last_name" label={t('resources.users.fields.last_name')} allowsSorting />
            <Table.Head id="email" label={t('resources.users.fields.email')} />
            <Table.Head id="status" label={t('resources.users.fields.status')} />
          </Table.Header>

          <AriaTableBody>
            {users.map((user) => {
              const status = getUserStatus(user)
              return (
                <Table.Row
                  key={user.id}
                  id={user.id}
                  className="cursor-pointer"
                  onAction={() => handleRowClick(user)}
                >
                  {/* ID */}
                  <Table.Cell>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {user.id}
                    </span>
                  </Table.Cell>

                  {/* First Name */}
                  <Table.Cell>
                    <span className="text-sm text-gray-900 dark:text-gray-100">
                      {user.first_name}
                    </span>
                  </Table.Cell>

                  {/* Last Name */}
                  <Table.Cell>
                    <span className="text-sm text-gray-900 dark:text-gray-100">
                      {user.last_name}
                    </span>
                  </Table.Cell>

                  {/* Email */}
                  <Table.Cell>
                    <a
                      href={`mailto:${user.email}`}
                      className="text-sm text-brand-600 hover:text-brand-700 hover:underline dark:text-brand-400"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {user.email}
                    </a>
                  </Table.Cell>

                  {/* Status */}
                  <Table.Cell>
                    <div className="flex items-center gap-2">
                      <StatusBadge
                        status={status === 'disabled' ? 'inactive' : status}
                        label={
                          status === 'active'
                            ? t('resources.users.active_tag')
                            : status === 'pending'
                              ? t('resources.users.invite_tag')
                              : t('resources.users.disabled_tag')
                        }
                      />
                      {/* Send/Resend invite button for non-active users */}
                      {!user.is_active && (
                        <Button
                          color="secondary"
                          size="sm"
                          onPress={() => {
                            setResendingUserId(user.id)
                            resendInvite.mutateAsync(user.id)
                              .then(() => toast.success(t('resources.users.send_invite_success')))
                              .catch((error: unknown) => {
                                const message = error instanceof Error ? error.message : t('resources.users.send_invite_error')
                                toast.error(message)
                              })
                              .finally(() => setResendingUserId(null))
                          }}
                          isDisabled={resendingUserId === user.id}
                          iconLeading={Send01Icon}
                        >
                          {resendingUserId === user.id
                            ? t('resources.users.sending_invite')
                            : status === 'pending'
                              ? t('resources.users.resend_invite')
                              : t('resources.users.send_invite')}
                        </Button>
                      )}
                    </div>
                  </Table.Cell>
                </Table.Row>
              )
            })}
          </AriaTableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <PaginationTable
          page={page}
          total={totalPages}
          perPage={perPage}
          onPageChange={handlePageChange}
          onPerPageChange={handlePageSizeChange}
        />
      )}
    </TableCard.Root>
  )
}

export default UsersPage
